import { notifyIcon, requestMethod } from "../../config";
import config from "../../env.config";
import { checkApiSuccess, getNotification } from "../../utils/common";
import { fetch } from "./api.service";

const PROJECT_VARIABLE_URL = `${config.BASE_URL.BASE_API_URL}/projectVariables`;

/**
 * @param {string|number} projectId
 * @returns {Promise<Array|null>}
 */
export const getProjectVariables = async (projectId, formId = null) => {
  if (projectId == null || projectId === "") return null;
  const requestConfig = {
    method: requestMethod.GET,
    url: `${PROJECT_VARIABLE_URL}/getProjectVariables`,
    params: { projectId, formId },
  };
  const response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return Array.isArray(response.data.data) ? response.data.data : [];
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

/**
 * @param {string} id — Persisted project variable id (Mongo ObjectId string)
 * @param {string|number} [projectId] — Team project id when the API requires scope
 * @returns {Promise<object|null>}
 */
export const getProjectVariableById = async (id, projectId = null) => {
  if (id == null || id === "") return null;
  const params = { id };
  if (projectId != null && projectId !== "") {
    params.projectId = projectId;
  }
  const requestConfig = {
    method: requestMethod.GET,
    url: `${PROJECT_VARIABLE_URL}/getProjectVariableById`,
    params,
  };
  const response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

/**
 * @param {Object} data — e.g. { projectId, name, type, description, attributes, adminType, adminCustomVariables, variableId }
 */
export const addProjectVariable = async (data) => {
  const requestConfig = {
    method: requestMethod.POST,
    url: `${PROJECT_VARIABLE_URL}/addProjectVariable`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

/**
 * @param {Object} data — must include persisted `id` / `_id` plus updatable fields
 */
export const updateProjectVariable = async (data) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${PROJECT_VARIABLE_URL}/updateProjectVariable`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

/**
 * @param {string} id — Mongo id of the project variable
 */
export const deleteProjectVariable = async (id) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${PROJECT_VARIABLE_URL}/deleteProjectVariable`,
    data: { id },
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

/**
 * @param {{ projectId: string|number, projectVariableId: string }} params
 * @returns {Promise<{ dimensions: object[], options: object[] }|null>}
 */
export const getProjectVariableDimensionAndOptions = async (params) => {
  const requestConfig = {
    method: requestMethod.GET,
    url: `${PROJECT_VARIABLE_URL}/getDimensionAndOptions`,
    params,
  };
  const response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response)) {
    const d = response.data?.data;
    return {
      dimensions: Array.isArray(d?.dimensions) ? d.dimensions : [],
      options: Array.isArray(d?.options) ? d.options : [],
    };
  }
  return null;
};

export const addProjectVariableDimension = async (data) => {
  const requestConfig = {
    method: requestMethod.POST,
    url: `${PROJECT_VARIABLE_URL}/addProjectVariableDimension`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const updateProjectVariableDimension = async (data) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${PROJECT_VARIABLE_URL}/updateProjectVariableDimension`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const deleteProjectVariableDimension = async (data) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${PROJECT_VARIABLE_URL}/deleteProjectVariableDimension`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const addProjectVariableOption = async (data) => {
  const requestConfig = {
    method: requestMethod.POST,
    url: `${PROJECT_VARIABLE_URL}/addProjectVariableOption`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const updateProjectVariableOption = async (data) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${PROJECT_VARIABLE_URL}/updateProjectVariableOption`,
    data,
  };
  const response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const deleteProjectVariableOption = async (data) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${PROJECT_VARIABLE_URL}/deleteProjectVariableOption`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};

export const deleteAllProjectVariableOptions = async (data) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${PROJECT_VARIABLE_URL}/deleteAllOptions`,
    data,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response) && response.data?.data != null) {
    return response.data.data;
  }
  if (response?.data?.message) {
    getNotification(response.data.message, notifyIcon.ERROR_ICON);
  }
  return null;
};
